import { createTheme, Theme } from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import {
  appFontFamily,
  colorBlack,
  colorWhite,
  elevatedSurfaceBoxShadow,
  layoutContentMaxWidthPx,
  productCardBadge,
  transition,
} from '../shared/style-settings'

/** Dialog paper: slightly deeper than cards so modals read above content */
const dialogPaperShadow = '0 12px 48px rgba(0, 0, 0, 0.14)'

/** Must match `palette.primary.main` — used in overrides before `theme` exists */
const primaryMain = '#1E32FA'

/** Same breakpoint as `theme.breakpoints.down('sm')` in default MUI breakpoints */
const mobileHeadingMedia = '@media (max-width: 599.95px)'

const mobileHeadingScale = {
  h1: { fontSize: '2.5rem', lineHeight: 1.2 },
  h2: { fontSize: '2rem', lineHeight: 1.25 },
  h3: { fontSize: '1.75rem', lineHeight: 1.3 },
  h4: { fontSize: '1.35rem', lineHeight: 1.35 },
  h5: { fontSize: '1.15rem', lineHeight: 1.4 },
  h6: { fontSize: '1.05rem', lineHeight: 1.45 },
} as const

const smDownHeading = (def: {
  fontSize: string
  lineHeight: number
}): Record<string, { fontSize: string; lineHeight: number }> => ({
  [mobileHeadingMedia]: def,
})

/** MUI’s `maxWidthLg` applies `maxWidth` *inside* `breakpoints.up('lg')` — same key required for overrides. */
const muiBreakpointHelper = createTheme({})

const theme: Theme = createTheme({
  props: {
    MuiCard: {
      elevation: 0,
    },
  },
  typography: {
    fontFamily: appFontFamily,
    body1: {
      fontSize: '0.9rem',
      fontWeight: 500,
    },
    h1: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h1) },
    h2: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h2) },
    h3: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h3) },
    h4: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h4) },
    h5: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h5) },
    h6: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h6) },
  },
  palette: {
    common: {
      white: colorWhite,
      black: colorBlack,
    },
    primary: {
      main: primaryMain,
    },
    success: {
      main: '#1ADBAD',
    },
    grey: {
      50: '#dedfe3',
      100: '#c5c6ce',
      200: '#a6a7b5',
      300: '#818398',
      400: '#5f6177',
      500: '#48495b',
      600: '#363745',
      700: '#272730',
      800: '#191a1f',
      900: '#101113',
      A100: '#b0b1bf',
      A200: '#8789a1',
      A400: '#55576d',
      A700: '#32333e',
    },
    info: {
      main: '#1396ff',
    },
  },
  // App-wide "dense" field typography: slightly smaller type and semibold weight for inputs,
  // outlined labels (including shrink state), adornments, select display text, MenuItem rows,
  // Autocomplete popup rows (options, empty/loading messages, group labels), and
  // FormControlLabel captions (checkbox, radio, switch).
  overrides: {
    MuiInputBase: {
      input: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        '&::placeholder': {
          opacity: 0.58,
        },
        '&:-ms-input-placeholder': {
          opacity: 0.58,
        },
      },
      textarea: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        '&::placeholder': {
          opacity: 0.58,
        },
        '&:-ms-input-placeholder': {
          opacity: 0.58,
        },
      },
    },
    /** Outlined fields: higher-specificity placeholder (wins over InputBase in some builds) */
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        transition: transition,
      },
      input: {
        '&::placeholder': {
          opacity: 0.58,
        },
        '&:-ms-input-placeholder': {
          opacity: 0.58,
        },
      },
    },
    /**
     * Outlined TextField often uses `label`, not `placeholder` — the gray “Title” text is
     * InputLabel before shrink, not ::placeholder (so MuiInputBase input placeholder never applies).
     */
    MuiInputLabel: {
      outlined: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        '&:not(.MuiInputLabel-shrink):not(.Mui-error)': {
          color: fade('#000000', 0.48),
        },
        '&$shrink': {
          fontSize: '0.8125rem',
          fontWeight: 600,
        },
      },
    },
    MuiInputAdornment: {
      root: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
    },
    MuiSelect: {
      select: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
    },
    MuiAutocomplete: {
      option: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
      loading: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
      noOptions: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
      groupLabel: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
    },
    MuiFormControlLabel: {
      label: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
    },
    MuiAlert: {
      message: {
        fontWeight: 600,
      },
    },
    MuiCssBaseline: {
      '@global': {
        p: {
          fontFamily: appFontFamily,
          fontSize: '0.9rem',
          fontWeight: 500,
        },
      },
    },
    /**
     * Text badges on cards (e.g. listed item). Excludes `variant="dot"` (notifications bell).
     * Edit shared tokens in `productCardBadge` (`style-settings.ts`) or override here.
     */
    MuiBadge: {
      badge: {
        '&:not(.MuiBadge-dot)': {
          fontSize: productCardBadge.fontSize,
          lineHeight: productCardBadge.lineHeight,
          letterSpacing: productCardBadge.letterSpacing,
          fontWeight: productCardBadge.fontWeightBadge,
          textTransform: productCardBadge.textTransform,
          padding: productCardBadge.muiBadgePadding,
          borderRadius: 4,
          boxShadow: elevatedSurfaceBoxShadow,
          color: colorWhite,
        },
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        '&:disabled': {
          boxShadow: 'none',
        },
      },
      containedPrimary: {
        borderRadius: 8,
        padding: '8px 20px',
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
        boxShadow: `0 4px 12px ${primaryMain}30`,
        transition: transition,
        '&:hover': {
          boxShadow: `0 6px 16px ${primaryMain}40`,
          transform: 'translateY(-2px)',
        },
        '@media (hover: none)': {
          '&:hover': {
            transform: 'none',
          },
        },
        '&:disabled': {
          boxShadow: 'none',
          transform: 'none',
        },
      },
    },
    MuiPaginationItem: {
      root: {
        '&.Mui-disabled': {
          boxShadow: 'none',
        },
      },
    },
    MuiTypography: {
      body1: {
        fontSize: '0.9rem',
        fontWeight: 500,
      },
      h1: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h1) },
      h2: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h2) },
      h3: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h3) },
      h4: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h4) },
      h5: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h5) },
      h6: { fontWeight: 700, ...smDownHeading(mobileHeadingScale.h6) },
    },
    MuiCard: {
      root: {
        borderRadius: 8,
        '&.MuiPaper-elevation0': {
          boxShadow: elevatedSurfaceBoxShadow,
        },
        '&.MuiPaper-outlined': {
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 8,
        boxShadow: dialogPaperShadow,
        fontFamily: appFontFamily,
      },
    },
    MuiDialogTitle: {
      root: {
        position: 'relative',
        padding: '16px 24px 12px',
        '& h2': {
          fontFamily: appFontFamily,
          fontSize: '1.125rem',
          fontWeight: 600,
          lineHeight: 1.4,
          letterSpacing: '-0.01em',
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
    },
    MuiDialogContent: {
      root: {
        padding: '8px 24px 16px',
        fontFamily: appFontFamily,
        fontSize: '0.875rem',
        lineHeight: 1.6,
        color: 'rgba(0, 0, 0, 0.6)',
      },
    },
    MuiDialogActions: {
      root: {
        fontFamily: appFontFamily,
        padding: '12px 16px 16px',
      },
    },
    MuiBreadcrumbs: {
      root: {
        fontSize: '0.8125rem',
        lineHeight: 1.35,
        marginBottom: 16,
        '& .MuiTypography-root': {
          fontSize: 'inherit',
        },
        '& .MuiLink-root': {
          fontSize: 'inherit',
        },
        '& li:last-child .MuiTypography-root': {
          fontWeight: 600,
        },
        '& a.MuiLink-root:hover': {
          textDecoration: 'underline',
        },
      },
    },
    MuiContainer: {
      maxWidthLg: {
        [muiBreakpointHelper.breakpoints.up('lg')]: {
          maxWidth: layoutContentMaxWidthPx,
        },
      },
    },
  } as any,
})

export default theme
